/* eslint-disable spire/export-styles */
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import { SafeDictionary } from "@insite/client-framework/Common/Types";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import logger from "@insite/client-framework/Logger";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { canAddToCart, hasEnoughInventory } from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import translate from "@insite/client-framework/Translate";
import TextField, { TextFieldPresentationProps, TextFieldProps } from "@insite/mobius/TextField";
import { validateQuantityField } from "@ucs/Helpers/FormFieldsValidation";
import React, { ChangeEvent, useEffect, useState } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

interface OwnProps extends TextFieldProps {
    configurationCompleted?: boolean;
    configurationSelection?: SafeDictionary<string>;
    variantSelectionCompleted?: boolean;
    labelOverride?: React.ReactNode;
    extendedStyles?: TextFieldPresentationProps;
}

const mapStateToProps = (
    state: ApplicationState,
    props: {
        configurationCompleted?: boolean;
        variantSelectionCompleted?: boolean;
        configurationSelection?: SafeDictionary<string>;
    } & HasProductContext,
) => {
    return {
        productSettings: getSettingsCollection(state).productSettings,
        canAddToCart: canAddToCart(
            state,
            props.productContext.product,
            props.productContext.productInfo,
            props.configurationCompleted,
            props.variantSelectionCompleted,
            props.configurationSelection,
        ),
        hasEnoughInventory: hasEnoughInventory(state, props.productContext),
    };
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & HasProductContext;

export const productQuantityOrderedStyles: TextFieldPresentationProps = {
    cssOverrides: {
        formInputWrapper: css`
            input[type="number"]::-webkit-outer-spin-button,
            input[type="number"]::-webkit-inner-spin-button {
                /* ISC-11023 deal with this. */
                /* stylelint-disable-next-line */
                -webkit-appearance: none;
                margin: 0;
            }
            margin-top: 4px !important;
        `,
        formField: css`
            margin-top: 15px;
            label {
                font-weight: 800;
                font-size: 12px;
                min-height: auto !important;
            }
        `,
        inputSelect: css`
            /* ISC-11023 deal with this. */
            /* stylelint-disable-next-line */
            -moz-appearance: textfield;
        `,
    },
};

const ProductQuantityOrdered = ({
    productSettings,
    hasEnoughInventory,
    productContext: {
        product: { id: productId, isVariantParent },
        onQtyOrderedChanged,
        productInfo: { qtyOrdered, failedToLoadInventory },
    },
    labelOverride,
    extendedStyles,
    canAddToCart,
    configurationCompleted,
    configurationSelection,
    variantSelectionCompleted,
    ...otherProps
}: Props) => {
    const [styles] = useState(() => mergeToNew(productQuantityOrderedStyles, extendedStyles));
    const [productQty, setProductQty] = useState(qtyOrdered.toString());
    const [dynamicStyles, setDynamicStyles] = useState(() => mergeToNew(productQuantityOrderedStyles, extendedStyles));
    const [currentWidth, setCurrentWidth] = useState("");

    const [quantityError, setQuantityError] = useState("");

    const setInputStyles = () => {
        const val = productQty ? parseInt(productQty, 10) : 1;
        const width = val < 100 ? "62px" : val < 1000 ? "70px" : val < 10000 ? "78px" : "92px";
        if (width !== currentWidth) {
            setCurrentWidth(width);
            setDynamicStyles({
                labelProps: {
                    variant: "legend",
                    ...styles.labelProps,
                },
                cssOverrides: {
                    ...styles.cssOverrides,
                    inputSelect: css`
                        width: ${width};
                        ${styles.cssOverrides?.inputSelect}
                    `,
                },
            });
        }
    };

    const validateQuantity = () => {
        const errorMessage = validateQuantityField(productQty);
        setQuantityError(errorMessage);
        return !errorMessage;
    };

    useEffect(() => {
        setInputStyles();
    }, []);

    useEffect(() => {
        setInputStyles();
        if (typeof productQty !== "undefined") {
            validateQuantity();
        }
    }, [productQty]);

    useEffect(() => {
        if (qtyOrdered === Number(productQty)) {
            return;
        }
        setProductQty(`${qtyOrdered || ""}`);
    }, [qtyOrdered]);

    // UFG-558: This block of code is disabled because client wants to see it as it was before upgrade(See ticket comments).
    // if ((!hasEnoughInventory && !failedToLoadInventory) || !canAddToCart) {
    //     return null;
    // }

    const keyPressHandler = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === "+") {
            event.preventDefault();
        }
        if (event.which === 13) {
            (event.target as any).blur();
        }
    };

    const qtyChangeHandler = (event: ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        setProductQty(value);

        if (!onQtyOrderedChanged) {
            logger.warn(`There was no onQtyOrderedChanged passed to the ProductContext for ${productId}`);
            return;
        }

        const possibleNumber = Number(value);
        if (!Number.isNaN(possibleNumber)) {
            onQtyOrderedChanged(possibleNumber);
        }
    };

    if (isVariantParent) {
        return null;
    }

    return (
        <TextField
            label={labelOverride ?? translate("Quantity")}
            value={productQty}
            type="number"
            error={quantityError}
            min={0}
            max={999999}
            pattern="[0-9]*"
            data-test-selector="product_qtyOrdered"
            {...otherProps}
            onKeyPress={keyPressHandler}
            onChange={qtyChangeHandler}
            {...dynamicStyles}
        />
    );
};

export default withProductContext(connect(mapStateToProps)(ProductQuantityOrdered));